import { chunk } from "lodash";

import React, { useCallback, useEffect, useRef, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlightIcon from "@mui/icons-material/Flight";
import { Accordion, AccordionSummary, Button, Typography } from "@mui/material";

import OptionDialog from "@/components/OptionDialog";
import { FlightItem as FlightItemType } from "@/typings";

import FlightTable from "./FlightTable";

interface FlightListProps {
  data: FlightItemType[];
  hiddenHead?: boolean;
  needMore?: boolean;
  onOpenRouteDialog?: (v: FlightItemType) => void;
  partialDisplay?: boolean;
}

const FlightList = ({ data, hiddenHead = false, needMore, onOpenRouteDialog, partialDisplay=false }: FlightListProps) => {
  const pageSize = useRef(25);
  const currentPage = useRef(0);
  const chunkData = useRef<FlightItemType[][]>([]);
  const [listData, setListData] = useState<FlightItemType[]>([]);

  const [optionDialogData, setOptionDialogData] = useState<FlightItemType>();

  const handleOpenOptionDialog = useCallback((data: FlightItemType) => {
    setOptionDialogData(data);
  }, []);

  const handlePaginationChange = useCallback(() => {
    currentPage.current = currentPage.current + 1;
    setListData([...listData, ...chunkData.current[currentPage.current]]);
  }, [listData]);

  useEffect(() => {
    if (needMore) {
      chunkData.current = chunk(data, pageSize.current);
      currentPage.current = 0;

      if (chunkData.current[currentPage.current]) {
        setListData([...chunkData.current[currentPage.current]]);
      }
    } else {
      setListData(data);
    }
  }, [data, needMore]);

  return (
    <div>
      <FlightTable
        hiddenHead={hiddenHead}
        data={listData}
        onOpenOptionDialog={handleOpenOptionDialog}
        onOpenRouteDialog={onOpenRouteDialog}
        partialDisplay={partialDisplay}
      />

      {listData.length > 0 && currentPage.current < chunkData.current.length - 1 ? (
        <Accordion
          expanded={false}
          sx={{
            boxShadow: "none",
            marginTop: "-1px",
          }}
        >
          <AccordionSummary onClick={(e) => e.stopPropagation()}>
            <Button
              className="w-[100%] h-[100%] mx-auto flex items-center justify-center"
              onClick={handlePaginationChange}
              variant="text"
              color="primary"
              startIcon={<ExpandMoreIcon />}
              endIcon={<FlightIcon className="rotate-[45deg]" />}
            >
              <Typography color="textPrimary" className="!pr-[4px]">
                More Flights{" "}
              </Typography>
            </Button>
          </AccordionSummary>
        </Accordion>
      ) : null}

      <OptionDialog
        data={optionDialogData}
        onClose={() => {
          setOptionDialogData(undefined);
        }}
      />
    </div>
  );
};

export default React.memo(FlightList);
